import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ArrowRight, ChevronLeft, ChevronRight, RefreshCw, TriangleAlert, Zap } from 'lucide-react';
import * as hr from '../api';
import Loader from '../components/Loader';
import { useHR } from '../HRContext';
import Modal from '../components/Modal';
import { DateInput, MonthInput } from '../components/Controls';
import Icon from '../Icon';
import { hrSubUrl } from '../catalog';
import { fmtMoney, fmtNumber } from '../format';
import { OPEN, STAGES, StageBadge, WARN, WarnBadge, lastDay, shiftMonth, thisMonth, yesterday } from './missed/shared';

// Time & Attendance > Missed Days Management (Stephanet's MissedDaysDashboard): the month's missed days, absence warnings, review
// status and payroll impact; Generate detects missed days from attendance, Refresh cancels those the attendance now covers.

export default function MissedDaysDashboard() {
  const { notify } = useHR();
  const navigate = useNavigate();
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [generating, setGenerating] = useState(false);

  const load = useCallback(() => hr.mdDashboard(month).then(d => { setData(d); setError(''); }).catch(e => setError(e.message)), [month]);
  useEffect(() => { setData(null); load(); }, [load]);

  // drill into Review + Approval (status filter), limited to this month unless the caller says otherwise
  const review = (params = {}, scoped = true) => () => navigate(hrSubUrl('missed-days-review', { ...(scoped ? { month } : {}), ...params }));
  const go = key => () => navigate(hrSubUrl(key));

  const refresh = async () => {
    setBusy('refresh');
    try {
      const r = await hr.mdRefresh(month);
      notify(r.cancelled ? `${r.cancelled} missed day(s) cancelled — attendance now recorded. ${r.open} still open.` : `Checked ${r.checked} missed day(s) against attendance — no changes.`);
      await load();
    } catch (err) { notify(err.message, 'bad'); }
    setBusy('');
  };

  if (error) return <div className="mdr"><div className="mdr-card mdr-empty">{error}</div></div>;
  if (!data) return <Loader title="Loading missed days…" subtitle="Fetching missed day records" />;
  const k = data.kpis;
  const w = data.warnings;
  const canGenerate = data.can.generate;

  const kpis = [
    ['Total Missed This Month', fmtNumber(k.total), 'blue', 'activity', data.label, review()],
    ['Pending HR Review', fmtNumber(k.pending), 'amber', 'clock', k.auto ? `${k.auto} auto generated` : 'Awaiting review', review({ status: 'Auto Generated,Pending HR Review' })],
    ['Approved (Paid)', fmtNumber(k.paid), 'green', 'circle-check', 'No deduction', review({ status: 'Approved - Paid Without Deduction,Biometric Error - Paid' })],
    ['Assigned to Leave', fmtNumber(k.leave), 'indigo', 'calendar-days', 'Charged to leave', review({ status: 'Approved - Assigned to Leave' })],
    ['Declined / Deducted', fmtNumber(k.deducted), 'red', 'circle-x', 'Salary deduction', review({ status: 'Declined - Salary Deduction Required' })],
    ['Total Deduction Amount', fmtMoney(k.deduction_amount), 'orange', 'banknote', 'Payroll impact', review({ status: 'Declined - Salary Deduction Required' })],
    ['Sent for Explanation', fmtNumber(k.sent), 'purple', 'users', k.escalated ? `${k.escalated} escalated` : 'Awaiting employee', review({ status: 'Sent to Employee,Escalated' })],
    ['Biometric Error Cases', fmtNumber(k.biometric), 'teal', 'fingerprint', 'Paid, device fault', review({ status: 'Biometric Error - Paid' })],
  ];
  const actions = [
    ['Review Pending', 'clipboard-check', 'amber', review({ status: OPEN.join(',') })],
    ['Run Report', 'bar-chart-3', 'blue', go('missed-days-report')],
    ['Assign Leave', 'palmtree', 'indigo', go('missed-days-leave')],
    ['Payroll Impact', 'wallet', 'orange', go('missed-days-payroll')],
    ['Employee History', 'history', 'green', go('missed-days-history')],
    ['Audit Trail', 'scroll-text', 'purple', go('missed-days-audit')],
    ['Settings', 'settings', 'slate', go('missed-days-settings')],
    ['Generate Now', 'zap', 'red', canGenerate ? () => setGenerating(true) : null],
  ];
  const statuses = data.statuses.map(x => ({ ...x, short: STAGES[x.name]?.[2] || x.name, color: STAGES[x.name]?.[1] || '#64748b' }));
  const s = data.settings;

  return (
    <div className="md">
      <div className="e360-head md-head">
        <div className="e360-head-left">
          <button className="back-btn" title="Previous month" onClick={() => setMonth(shiftMonth(month, -1))}><ChevronLeft size={17} /></button>
          <MonthInput className="att-date" value={month} onChange={e => e.target.value && setMonth(e.target.value)} />
          <button className="back-btn" title="Next month" disabled={month >= thisMonth()} onClick={() => setMonth(shiftMonth(month, 1))}><ChevronRight size={17} /></button>
          <div className="md-title"><b>Missed Days Management</b><span>HR / Attendance / Payroll Control — {data.label}</span></div>
        </div>
        <div className="e360-head-actions">
          {canGenerate && <button className="btn" disabled={busy === 'refresh'} onClick={refresh}><RefreshCw size={15} className={busy === 'refresh' ? 'spin' : ''} /> {busy === 'refresh' ? 'Refreshing…' : 'Refresh Attendance'}</button>}
          {canGenerate && <button className="btn primary" onClick={() => setGenerating(true)}><Zap size={15} /> Generate Missed Days</button>}
        </div>
      </div>

      {w.employees > 0 && (
        <div className="md-alert">
          <span className="md-alert-icon"><TriangleAlert size={18} /></span>
          <div className="grow">
            <b>{w.employees} Employee(s) with Absence Warnings</b>
            <p>{w.hr_query} require HR query. {w.mgmt} require management review.
              <button className="md-link" onClick={review({ status: OPEN.join(',') })}>Review Now <ArrowRight size={13} /></button></p>
            <div className="md-alert-people">
              {w.list.map(p => (
                <button key={p.employee_id} className={`md-person w-${WARN[p.level]}`} title={`${p.level} · ${p.days} day(s) this month`}
                        onClick={() => navigate(hrSubUrl('employee-360', { id: p.employee_id }))}>
                  {p.full_name} <small>{p.days}d</small>
                </button>
              ))}
            </div>
          </div>
          <small className="md-alert-rule">Thresholds per month: Warning {s.warning} · HR Query {s.hr_query} · Management {s.mgmt}</small>
        </div>
      )}

      <div className="md-kpis">
        {kpis.map(([label, value, color, icon, note, open]) => (
          <button key={label} type="button" className={`md-kpi lc-${color}`} disabled={!open} onClick={open || undefined}>
            <div className="md-kpi-head"><span>{label}</span><Icon name={icon} size={17} /></div>
            <b>{value}</b>
            <small>{open ? <>Click to view <ArrowRight size={11} /></> : note}</small>
          </button>
        ))}
      </div>

      <div className="xp-two md-row">
        <div className="panel">
          <h3 className="panel-title">Monthly Trend: Missed Days vs Actions</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.trend} margin={{ left: -10, right: 8, top: 8 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--line)" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip formatter={v => `${fmtNumber(v)} day(s)`} /><Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="total" name="Total Missed" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              <Bar dataKey="approved" name="Approved" fill="#16a34a" radius={[4, 4, 0, 0]} />
              <Bar dataKey="deducted" name="Deducted" fill="#dc2626" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="panel">
          <h3 className="panel-title">Missed Days by Status — {data.label}</h3>
          {statuses.length ? (
            <div className="md-pie">
              <ResponsiveContainer width="100%" height={230}>
                <PieChart>
                  <Pie data={statuses} dataKey="value" nameKey="name" innerRadius={55} outerRadius={92} paddingAngle={2} stroke="#fff">
                    {statuses.map(x => <Cell key={x.name} fill={x.color} />)}
                  </Pie>
                  <Tooltip formatter={v => `${v} record(s)`} />
                </PieChart>
              </ResponsiveContainer>
              <ul className="md-legend">
                {statuses.map(x => <li key={x.name}><i style={{ background: x.color }} /><span>{x.short}</span><b>{x.value}</b></li>)}
              </ul>
            </div>
          ) : <div className="empty">No missed days in {data.label}.</div>}
        </div>
      </div>

      <div className="xp-two md-row">
        <div className="panel">
          <h3 className="panel-title">Missed Days by Department</h3>
          {data.departments.length ? (
            <ResponsiveContainer width="100%" height={Math.max(200, data.departments.length * 34)}>
              <BarChart data={data.departments} layout="vertical" margin={{ left: 10, right: 16 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--line)" />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} /><YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={120} />
                <Tooltip formatter={v => `${fmtNumber(v)} day(s)`} /><Bar dataKey="value" name="Missed days" fill="#6366f1" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="empty">No missed days in {data.label}.</div>}
        </div>
        <div className="panel">
          <h3 className="panel-title">Quick Actions</h3>
          <div className="md-actions">
            {actions.map(([label, icon, color, open]) => (
              <button key={label} type="button" className={`md-action lc-${color}`} disabled={!open} onClick={open || undefined}>
                <Icon name={icon} size={16} /> {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="panel">
        <div className="toolbar">
          <h3 className="panel-title">Recent Missed Days Activity</h3>
          <div className="spacer" />
          <button className="btn small" onClick={review()}>View All</button>
        </div>
        <div className="table-wrap">
          <table className="xp-table md-table">
            <thead><tr><th>ID</th><th>Employee</th><th>Department</th><th>Date</th><th>Status</th><th>Warning Level</th><th>Recommendation</th><th>Deduction</th></tr></thead>
            <tbody>
              {data.recent.map(r => (
                <tr key={r.id} className="clickable" onClick={review({ q: r.reference }, false)}>
                  <td className="mono">{r.reference}</td>
                  <td><b>{r.employee}</b><small className="md-sub">{r.employee_no}</small></td>
                  <td>{r.department}</td>
                  <td className="mono">{r.date}</td>
                  <td><StageBadge value={r.status} /></td>
                  <td><WarnBadge value={r.warning_level} /></td>
                  <td className="wrap md-rec">{r.recommendation || '—'}</td>
                  <td className={`num ${r.deduction ? 'md-neg' : ''}`}>{r.deduction ? fmtMoney(r.deduction) : '—'}</td>
                </tr>
              ))}
              {!data.recent.length && <tr><td colSpan={8} className="empty">No missed days recorded in {data.label}.{canGenerate ? ' Use Generate Missed Days to detect them from attendance.' : ''}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {generating && <GenerateModal month={month} onClose={() => setGenerating(false)} notify={notify}
                                    onDone={m => { setGenerating(false); if (m !== month) setMonth(m); else load(); }} />}
    </div>
  );
}

// ---------------------------------------------------------------- generate from attendance
function GenerateModal({ month, onClose, onDone, notify }) {
  const y = yesterday();
  const [range, setRange] = useState(() => ({ start: `${month}-01`, end: [lastDay(month), y].sort()[0] }));
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const run = useCallback(async dryRun => {
    setBusy(true); setError('');
    try {
      const r = await hr.mdGenerate({ ...range, dry_run: dryRun });
      if (dryRun) setPreview(r);
      else { notify(`${r.created} missed day(s) generated for ${r.employees} employee(s)`); onDone(r.start.slice(0, 7)); }
    } catch (err) { setError(err.message); setPreview(null); }
    setBusy(false);
  }, [range, notify, onDone]);
  useEffect(() => { if (range.start && range.end) run(true); }, [range.start, range.end]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Modal title="Generate Missed Days" onClose={onClose}
           footer={<><button className="btn ghost" onClick={onClose}>Cancel</button><div className="spacer" />
             <button className="btn primary" disabled={busy || !preview?.count} onClick={() => run(false)}>
               <Zap size={15} /> {busy ? 'Working…' : preview?.count ? `Create ${preview.count} Missed Day${preview.count > 1 ? 's' : ''}` : 'Nothing to create'}</button></>}>
      <p className="muted small md-gen-intro">Scans working days (excluding public holidays, by the weekend and holiday rules) for active employees with no attendance, or marked absent, and no approved leave. New records start as <b>Auto Generated</b> for HR review.</p>
      <div className="form-grid cols-2">
        <div className="field"><span className="field-label">From</span>
          <DateInput value={range.start} onChange={e => e.target.value && setRange(r => ({ ...r, start: e.target.value }))} /></div>
        <div className="field"><span className="field-label">To</span>
          <DateInput value={range.end} onChange={e => e.target.value && setRange(r => ({ ...r, end: e.target.value }))} /></div>
      </div>
      {error && <div className="error md-gen-error">{error}</div>}
      {busy && !preview && <div className="muted md-gen-wait">Checking attendance…</div>}
      {preview && (
        <div className="md-gen">
          <div className="md-gen-stats">
            <div className={preview.count ? 'hot' : ''}><b>{preview.count}</b><span>Missed days found</span></div>
            <div><b>{preview.employees}</b><span>Employees</span></div>
            <div><b>{preview.captured_days}/{preview.working_days}</b><span>Working days with attendance</span></div>
            <div><b>{preview.already_recorded}</b><span>Already recorded</span></div>
          </div>
          {preview.uncaptured_days.length > 0 && (
            <div className="xp-note warn">Skipped {preview.uncaptured_days.length} working day(s) with no attendance captured for anyone: {preview.uncaptured_days.slice(0, 6).join(', ')}{preview.uncaptured_days.length > 6 ? '…' : ''}</div>
          )}
          {preview.top.length > 0 && (
            <table className="xp-table md-gen-table">
              <thead><tr><th>Employee</th><th>Employee ID</th><th>Days</th></tr></thead>
              <tbody>{preview.top.map(t => <tr key={t.employee_no}><td>{t.full_name}</td><td className="mono">{t.employee_no}</td><td className="num">{t.days}</td></tr>)}</tbody>
            </table>
          )}
          {!preview.count && <div className="xp-note info">No new missed days in this period — everyone is covered by attendance, leave or an existing record.</div>}
        </div>
      )}
    </Modal>
  );
}
